/**
 * EditableTicketSubject - click-to-edit ticket title in the agent
 * detail page header.
 *
 * Greg 2026-07-31: the subject is whatever the customer's mail client
 * put in the header ("Re: Fwd: Re: New "Sold Out" microsite needed"),
 * and after a merge or a re-scoped request it is often wrong. Every
 * other field in that header - status, priority, assignee - was already
 * mutable from the page; the title was the one thing that needed a
 * database edit to fix.
 *
 * Agent-only, by construction rather than by a flag: this component is
 * mounted from pages/agent/TicketDetailAgentPage only, that page only
 * renders for the agent / system_admin roles, and the PATCH behind
 * ``onSave`` sits behind ``require_agent`` server-side. The customer
 * detail page renders its subject as plain text and has no path here.
 *
 * Editing rules, in the order an agent runs into them:
 *   - Click the title (or the pencil) to edit. Enter saves, Escape
 *     reverts, clicking away saves.
 *   - Whitespace is collapsed to single spaces and trimmed. An email
 *     subject is one line, and a title pasted from anywhere else
 *     usually arrives with newlines in it.
 *   - Emptying the field reverts instead of erroring. The backend
 *     requires min_length=1, and "I selected all, deleted, then
 *     clicked away" is a mis-click, never a request to blank a title.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from "react";

import { Button } from "@/components/ui";

/** Mirrors TicketUpdate.subject in backend/app/schemas/ticket.py. The
 * cap is enforced server-side too; this only stops the agent typing
 * past it and getting a 422 on save. */
const MAX_SUBJECT_LENGTH = 500;

/** One line, single-spaced. Applied to both sides of the "did this
 * actually change" comparison so re-spacing alone is not a save. */
function collapseWhitespace(raw: string): string {
  return raw.replace(/\s+/g, " ").trim();
}

export function EditableTicketSubject({
  subject,
  onSave,
  canEdit = true,
}: {
  subject: string;
  /** Persist the new title. Rejects on failure so the editor can stay
   * open with the agent's text intact. */
  onSave: (next: string) => Promise<void>;
  canEdit?: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(subject);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Re-seed when the ticket underneath us changes - navigation to
  // another ticket, or the 15s detail poll landing a rename someone
  // else made. Deliberately not while editing, which would eat what
  // the agent is typing.
  useEffect(() => {
    if (!editing) setDraft(subject);
  }, [subject, editing]);

  // Caret at the end rather than a select-all: the common edit is
  // trimming an "Re: Fwd:" prefix or appending, not retyping.
  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  // Grow the textarea to fit. It behaves as a single-line field
  // (Enter saves) but wraps like the h1 it replaces, so a long title
  // does not scroll inside a one-row box.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, editing]);

  function startEditing(): void {
    if (!canEdit) return;
    setDraft(subject);
    setError(null);
    setEditing(true);
  }

  function cancel(): void {
    setDraft(subject);
    setError(null);
    setEditing(false);
  }

  async function commit(): Promise<void> {
    if (saving) return;
    const next = collapseWhitespace(draft);
    if (!next || next === collapseWhitespace(subject)) {
      cancel();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      setError("Could not save the title. Try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === "Enter") {
      // A ticket subject is one line, so Enter is "done", not a
      // newline - including Shift+Enter.
      event.preventDefault();
      void commit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      cancel();
    }
  }

  function handleBlur(event: FocusEvent<HTMLTextAreaElement>): void {
    // Our own Save / Cancel buttons preventDefault on mousedown so
    // they never steal focus, but a click on one still lands here in
    // browsers that focus buttons on click. Either way, leaving focus
    // inside this component is not "clicking away".
    if (containerRef.current?.contains(event.relatedTarget)) return;
    void commit();
  }

  if (!editing) {
    return (
      <h1
        className="min-w-0 text-xl font-semibold leading-tight text-neutral-900"
        data-testid="ticket-subject"
      >
        {canEdit ? (
          <button
            type="button"
            onClick={startEditing}
            title="Click to edit the title"
            aria-label={`Edit title: ${subject}`}
            data-testid="ticket-subject-edit"
            className="group -mx-1.5 -my-0.5 inline-flex items-start gap-2 rounded px-1.5 py-0.5 text-left hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          >
            <span>{subject}</span>
            <i
              className="fa-duotone fa-thin fa-pen-to-square mt-1 text-sm text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden="true"
            />
          </button>
        ) : (
          subject
        )}
      </h1>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full min-w-0"
      data-testid="ticket-subject-editor"
    >
      <textarea
        ref={inputRef}
        rows={1}
        value={draft}
        maxLength={MAX_SUBJECT_LENGTH}
        aria-label="Ticket title"
        data-testid="ticket-subject-input"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="block w-full resize-none overflow-hidden rounded-lg border-neutral-300 px-2 py-1 text-xl font-semibold leading-tight text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          isLoading={saving}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void commit()}
          data-testid="ticket-subject-save"
        >
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={saving}
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancel}
          data-testid="ticket-subject-cancel"
        >
          Cancel
        </Button>
        <span className="text-xs text-neutral-400">
          Enter to save, Esc to cancel
        </span>
        {error && (
          <span className="text-xs text-red-600" role="alert">
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

export default EditableTicketSubject;
